/**
 * Share comparison results: copy link + open Twitter/X share dialog.
 * Pure functions (no side effects on import) — call mountShareButton() to activate.
 */
import { STOREFRONTS } from '../data/storefronts.js';
import { t as defaultT } from '../i18n/index.js';

/**
 * Build the shareable URL for the current comparison.
 * @param {number|string} appId
 * @returns {string}
 */
export function buildShareUrl(appId) {
  const url = new URL(window.location.href);
  url.searchParams.set('appId', String(appId));
  // Strip compare param so the share link focuses on the primary app
  url.searchParams.delete('compareId');
  return url.toString();
}

/**
 * Build a Twitter/X intent URL for sharing.
 * @param {string} appName
 * @param {string} shareUrl
 * @param {(key: string, params?: Record<string, string|number>) => string} [translate]
 * @returns {string}
 */
export function buildTwitterUrl(appName, shareUrl, translate = defaultT) {
  const text = translate('share.twitterText', {
    appName,
    countryCount: STOREFRONTS.length,
  });
  const params = new URLSearchParams({ text, url: shareUrl });
  return `https://twitter.com/intent/tweet?${params.toString()}`;
}

/**
 * Copy text to clipboard. Falls back to prompt() on insecure contexts.
 * @param {string} text
 * @returns {Promise<boolean>} true if copy succeeded
 */
export async function copyToClipboard(text) {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (_) {
      // fall through to prompt fallback
    }
  }
  // Fallback for insecure contexts (e.g. HTTP)
  /* global prompt */
  prompt('Copy this link:', text);
  return false;
}

/**
 * Render and mount the share button row into a container element.
 * @param {HTMLElement} container
 * @param {{ appId: number|string, appName: string, t: Function }} opts
 */
export function mountShareButton(container, { appId, appName, t }) {
  const existingRow = container.querySelector('.share-row');
  if (existingRow) existingRow.remove();

  const row = document.createElement('div');
  row.className = 'share-row';

  const copyBtn = document.createElement('button');
  copyBtn.type = 'button';
  copyBtn.className = 'share-btn share-btn--copy';
  copyBtn.setAttribute('aria-label', t('share.copyAriaLabel'));
  copyBtn.textContent = t('share.copyLabel');

  const twitterBtn = document.createElement('a');
  twitterBtn.className = 'share-btn share-btn--twitter';
  twitterBtn.setAttribute('target', '_blank');
  twitterBtn.setAttribute('rel', 'noopener noreferrer');
  twitterBtn.setAttribute('aria-label', t('share.twitterAriaLabel'));
  twitterBtn.textContent = t('share.twitterLabel');

  function updateLinks() {
    const shareUrl = buildShareUrl(appId);
    twitterBtn.href = buildTwitterUrl(appName, shareUrl, t);
    return shareUrl;
  }

  updateLinks();

  copyBtn.addEventListener('click', async () => {
    const shareUrl = updateLinks();
    const success = await copyToClipboard(shareUrl);
    if (success) {
      const original = copyBtn.textContent;
      copyBtn.textContent = t('share.copied');
      copyBtn.setAttribute('aria-live', 'polite');
      setTimeout(() => {
        copyBtn.textContent = original;
        copyBtn.removeAttribute('aria-live');
      }, 2000);
    }
  });

  row.appendChild(copyBtn);
  row.appendChild(twitterBtn);
  container.appendChild(row);
}
